import React, { useEffect, useState } from "react";
import { Button, DownloadIcon } from "@pd/pd-ui-kit";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData } from "~/hooks/locationData";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { useNotify } from "~/shared/hooks/useNotify";
import { TableV2 } from "~/shared/ui/TableV2";

import DnmFilter from "./Filter";
import useGetDNMData from "./hooks/useGetDNMData";
import useGetDNMDataReport from "./hooks/useGetDNMDataReport";
import { getColumnsDnm, resultDataIProps } from "./model/columns";

import style from "./DNM.less";

const DNM = () => {
  const { t } = useTranslation();
  const [params, setParams] = useState<any>();
  const [resultData, setResultData] = useState<resultDataIProps[]>([]);
  const [switchRequest, setSwitchRequest] = useState(true);
  const [sortData, setSortData] = useState<resultDataIProps[]>([]);
  const [sortField, setSortField] = useState<string | undefined>("percentage");
  const [sortType, setSortType] = useState<"ASC" | "DESC" | undefined>("DESC");

  const columns = getColumnsDnm(
    t,
    sortField === "percentage"
      ? sortType === "ASC"
        ? "ascend"
        : sortType === "DESC"
          ? "descend"
          : null
      : null,
  );

  const { fio, position, coincidence, date, dealerSelect } =
    useGetLocationData() as Record<string, unknown>;
  const currentDealerId = useCurrentDealerId();

  useEffect(() => {
    const param = new URLSearchParams();
    const selectedDealers = dealerSelect
      ? String(dealerSelect).split(",").filter(Boolean)
      : [];
    const dealerIds = selectedDealers.length
      ? selectedDealers
      : currentDealerId
        ? [currentDealerId]
        : [];
    if (dealerIds.length) {
      dealerIds.forEach((id) => param.append("dealerId[]", id));
      setSwitchRequest(true);
    } else {
      setSwitchRequest(false);
    }
    const month = date
      ? String(date).slice(0, 7).replace("-", ".")
      : format(new Date(), "yyyy.MM");
    param.append("month", month);
    fio && param.append("fio", String(fio));
    position && param.append("position", String(position));
    setParams(param.toString());
  }, [fio, position, date, dealerSelect]);

  const { data, isLoading } = useGetDNMData(params, switchRequest);

  useEffect(() => {
    if (!isLoading && data?.matches.length > 0) {
      const result = data.matches;
      setResultData(() =>
        result.map((item) => {
          const employer = item.employee;

          const csvEntry = item.dmsEmployee;
          const fio = `${employer?.surname} ${employer?.name} ${employer?.middleName}`;
          const position = `${employer?.position}`;
          const status = employer?.status;
          const fioCsvEntry = `${csvEntry?.name}`;
          const positionCsvEntry = `${csvEntry?.position ?? employer.position}`;
          const count = csvEntry.count;
          const percentage = item.matchPercentage;
          return {
            fio: fio,
            email: employer?.email,
            dealer: employer?.dealer,
            position: position,
            status: status,
            fioCsvEntry: fioCsvEntry,
            emailCsvEntry: csvEntry?.email,
            dealerCsvEntry: csvEntry?.dealer,
            positionCsvEntry: positionCsvEntry,
            count: csvEntry.type === "worksheets" ? `${count}/` : `/${count}`,
            percentage: percentage,
            differentFields: item.differentFields,
            matchedFields: item.matchedFields,
            partialMatchFields: item.partialMatchFields,
          };
        }),
      );
    } else {
      setResultData([]);
    }
  }, [data]);

  useEffect(() => {
    const coincidenceValue = coincidence ? String(coincidence) : null;

    if (resultData.length) {
      const processedData = resultData.slice();
      if (sortField === "percentage" && sortType) {
        const sortDirection = sortType === "ASC" ? 1 : -1;
        processedData.sort(
          (a, b) =>
            sortDirection * (Number(a.percentage) - Number(b.percentage)),
        );
      }
      setSortData(
        coincidenceValue
          ? processedData.filter(
              (i) => Number(i.percentage) === Number(coincidenceValue),
            )
          : processedData,
      );
    } else {
      setSortData([]);
    }
  }, [resultData, data?.matches, sortField, sortType, coincidence]);

  const { notifyReport } = useNotify();
  const { refetch: refetchReport } = useGetDNMDataReport(params);

  const clickButtonReport = async () => {
    const result = switchRequest && (await refetchReport());

    if (result.data) {
      notifyReport(result.data.fileHash);
    }
  };

  return (
    <Wrapper.Content title={t("dnm.title")} filterComponent={<DnmFilter />}>
      <div className={style.actionRow}>
        <Button
          onClick={clickButtonReport}
          color={"secondary"}
          variant={"filled"}
          bold
          fontSize="12"
          size={"middle"}
          radius={30}
          icon={<DownloadIcon size={16} fill="#495a64" />}
        >
          {t("dnm.report")}
        </Button>
      </div>
      <TableV2<resultDataIProps>
        columns={columns}
        dataSource={sortData}
        loading={isLoading}
        pagination={false}
        wrapperClassName={style["dnm-table"]}
        noHorizontalScroll
        rowKey={(_, index) => String(index)}
        sortableColumns={["percentage"]}
        sortField={sortField}
        sortType={sortType}
        onSortChange={(field, type) => {
          setSortField(field);
          setSortType(type);
        }}
      />
    </Wrapper.Content>
  );
};

export default DNM;
